import { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart3, Target } from 'lucide-react';
import StrategyWorkspaceHeader from '../StrategyWorkspaceHeader';
import { useFormulationProgress } from '../../../hooks/useFormulationProgress';

// 🚀 LAZY LOAD FORMULATION COMPONENTS
const SummaryOfAnalysisPage = lazy(() => import('./SummaryOfAnalysisPage'));
const StrategiesPage = lazy(() => import('./StrategiesPage'));

// Loading component for formulation sections
const FormulationLoadingFallback = ({ sectionName }: { sectionName: string }) => (
  <div className="w-full h-full">
    <div className="p-8 space-y-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-8 py-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-b animate-pulse">
          <div className="h-8 w-64 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 w-96 max-w-full bg-gray-100 rounded"></div>
        </div>

        <div className="p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-6 animate-pulse">
              <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 w-full bg-gray-100 rounded"></div>
                <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
                <div className="h-4 w-1/2 bg-gray-100 rounded"></div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 animate-pulse">
              <div className="h-6 w-56 bg-gray-200 rounded mb-4"></div>
              <div className="h-32 bg-gray-100 rounded"></div>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center space-x-2 text-orange-600">
              <div className="animate-spin h-4 w-4 border-2 border-orange-600 border-t-transparent rounded-full"></div>
              <span className="text-sm font-medium">Loading {sectionName}...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function FormulationRouter() {
  const { companyId, subPage } = useParams<{ companyId: string; subPage?: string }>();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('summary');

  // Use shared formulation progress hook
  const { progress: formulationProgress, refreshProgress } = useFormulationProgress(
    companyId || ''
  );

  // Set active section based on sub-page
  useEffect(() => {
    if (subPage === 'strategies') {
      setActiveSection('strategies');
    } else if (subPage === 'summary') {
      setActiveSection('summary');
    } else {
      // Default to summary for undefined/empty subPage
      setActiveSection('summary');
    }
  }, [subPage]);

  // Trigger progress refresh when sections update
  const handleProgressUpdate = useCallback(() => {
    refreshProgress();
  }, [refreshProgress]);

  const summaryProgress = formulationProgress.summary;
  const strategiesProgress = formulationProgress.strategies;
  const overallProgress = formulationProgress.overall;

  // Render the content based on active section
  const renderContent = () => {
    if (activeSection === 'strategies') {
      return (
        <Suspense fallback={<FormulationLoadingFallback sectionName="Strategies" />}>
          <StrategiesPage onProgressChange={handleProgressUpdate} />
        </Suspense>
      );
    } else {
      return (
        <Suspense fallback={<FormulationLoadingFallback sectionName="Summary of Analysis" />}>
          <SummaryOfAnalysisPage onProgressChange={handleProgressUpdate} />
        </Suspense>
      );
    }
  };

  return (
    <div className="company-workspace-screen strategy-editor-page">
      <main>
        <StrategyWorkspaceHeader
          companyId={companyId || ''}
          step={4}
          stage="Strategy formulation"
          title="Turn insight into clear choices."
          description="Bring your analysis together and choose strategies that connect to your objectives."
          progress={overallProgress}
          activeSection={activeSection}
          sections={[
            { id: 'summary', label: 'Plan summary', icon: BarChart3, progress: summaryProgress },
            { id: 'strategies', label: 'Strategies', icon: Target, progress: strategiesProgress },
          ]}
          onSectionChange={(section) =>
            navigate(`/company/${companyId}/strategy/formulation/${section}`)
          }
          next={{ label: 'Put your plan to work', href: `/company/${companyId}/execution` }}
        />

        {/* Content Area - Scrollable */}
        <div className="strategy-editor-content">
          <div className="w-full h-full">{renderContent()}</div>
        </div>
      </main>
    </div>
  );
}
